import { __ } from '~@/Composables/translation'

/**
 * Normalizes section filters from the server's snake_case format to camelCase.
 *
 * @param {Object|null} rawFilters
 * @returns {Object}
 */
export function normalizeSectionFilters (rawFilters) {
    return {
        productGroupIds: rawFilters?.product_group_ids ?? null,
        retirementStatus: rawFilters?.retirement_status ?? null,
        fulfillmentAccountGroupIds: rawFilters?.fulfillment_account_group_ids ?? null,
        emailLanguages: rawFilters?.email_languages ?? null,
        smsLanguages: rawFilters?.sms_languages ?? null,
        customerIds: rawFilters?.customer_ids ?? null,
        settingsModels: rawFilters?.settings_models ?? null,
        productModels: rawFilters?.product_models ?? null,
    }
}

/**
 * Returns true if any non-default filters are configured for the given section.
 *
 * @param {string} section
 * @param {Object} filters - camelCase filters (see normalizeSectionFilters)
 * @returns {boolean}
 */
export function hasSnapshotSectionFilters (section, filters) {
    if (section === 'settings') {
        return filters.settingsModels !== null
    }
    if (section === 'products') {
        return filters.retirementStatus !== null ||
            filters.productGroupIds !== null ||
            filters.fulfillmentAccountGroupIds !== null ||
            filters.productModels !== null
    }
    if (section === 'email_templates') {
        return filters.emailLanguages !== null
    }
    if (section === 'sms_templates') {
        return filters.smsLanguages !== null
    }
    if (section === 'customers') {
        return filters.customerIds !== null
    }
    return false
}

/**
 * Returns a human-readable summary for a snapshot section's filter configuration.
 * Always returns a non-empty string (falls back to "All - No filters" when nothing is configured).
 *
 * @param {string} section
 * @param {Object} filters - camelCase filters (see normalizeSectionFilters)
 * @returns {string}
 */
export function getSnapshotSectionSummary (section, filters) {
    if (section === 'settings') {
        if (filters.settingsModels === null) {
            return __('tools.testing.snapshots.create.modal.form.preview_all_no_filters')
        }
        return __('tools.testing.snapshots.create.modal.form.preview_settings_models', { count: filters.settingsModels.length })
    }

    if (section === 'products') {
        if (!hasSnapshotSectionFilters(section, filters)) {
            return __('tools.testing.snapshots.create.modal.form.preview_all_no_filters')
        }

        const parts = []
        const status = filters.retirementStatus
        const groupCount = filters.productGroupIds !== null ? filters.productGroupIds.length : null

        if (status === 'none') {
            // no product groups — nothing to push; handled by fallback below
        } else if (groupCount === null) {
            if (status === 'active') {
                parts.push(__('tools.testing.snapshots.create.modal.form.preview_all_active_groups'))
            } else if (status === 'retired') {
                parts.push(__('tools.testing.snapshots.create.modal.form.preview_all_retired_groups'))
            } else {
                parts.push(__('tools.testing.snapshots.create.modal.form.preview_all_groups'))
            }
        } else {
            if (status === 'active') {
                parts.push(__('tools.testing.snapshots.create.modal.form.preview_active_from_count', { count: groupCount }))
            } else if (status === 'retired') {
                parts.push(__('tools.testing.snapshots.create.modal.form.preview_retired_from_count', { count: groupCount }))
            } else {
                parts.push(__('tools.testing.snapshots.create.modal.form.preview_groups_from_count', { count: groupCount }))
            }
        }

        if (filters.fulfillmentAccountGroupIds !== null) {
            parts.push(__('tools.testing.snapshots.create.modal.form.preview_account_groups', { count: filters.fulfillmentAccountGroupIds.length }))
        }
        if (filters.productModels !== null) {
            parts.push(__('tools.testing.snapshots.create.modal.form.preview_other_models', { count: filters.productModels.length }))
        }

        if (parts.length === 0) {
            return __('tools.testing.snapshots.create.modal.form.preview_no_groups')
        }

        return parts.join(', ')
    }

    if (section === 'email_templates') {
        if (filters.emailLanguages === null) {
            return __('tools.testing.snapshots.create.modal.form.preview_all_no_filters')
        }
        return __('tools.testing.snapshots.create.modal.form.preview_email_languages', { count: filters.emailLanguages.length })
    }

    if (section === 'sms_templates') {
        if (filters.smsLanguages === null) {
            return __('tools.testing.snapshots.create.modal.form.preview_all_no_filters')
        }
        return __('tools.testing.snapshots.create.modal.form.preview_sms_languages', { count: filters.smsLanguages.length })
    }

    if (section === 'customers') {
        if (filters.customerIds === null) {
            return __('tools.testing.snapshots.create.modal.form.preview_all_no_filters')
        }
        return __('tools.testing.snapshots.create.modal.form.preview_customers', { count: filters.customerIds.length })
    }

    return ''
}
